import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  AlertTriangle,
  Users,
  ArrowRight,
  ShieldAlert,
} from "../../../../components/icons";
import { Player, Team } from "../../types";
import {
  fetchLoggerWithAuth,
  LOGGER_API_URL,
  IS_E2E_TEST_MODE,
} from "../../../../lib/loggerApi";
import { formatDisplayName } from "../../../../lib/nameFormat";

/**
 * EPIC-95 T5: `useCockpitSubstitutionFlow.ts`'s `teamPlayerToLoggerPlayer`
 * coerces a squad player's still-nullable `jersey_number` (roster-imported,
 * never completed in Players) to the sentinel `0` so `Player.jersey_number`
 * can stay a required `number` everywhere else in the cockpit. No legitimate
 * jersey is ever `0` in this app (the roster-import review table and the
 * quick-create form both enforce `min={1}`), so `0` here always means
 * "missing" — never a real player's number.
 */
const JERSEY_MISSING = 0;
const formatJerseyBadge = (jerseyNumber: number): string =>
  jerseyNumber > JERSEY_MISSING ? String(jerseyNumber) : "—";

/**
 * Counts only. There is no substitution or window budget any more (client
 * decision 2026-08-18) — the backend stopped sending `max_substitutions` /
 * `remaining_substitutions` / `max_windows` / `remaining_windows`, and this
 * panel no longer shows a "remaining" figure. What still blocks a
 * substitution: a red-carded player on either side of the swap, and a
 * player who has already been substituted out coming back on.
 */
interface SubstitutionStatus {
  total_substitutions: number;
  windows_used: number;
  is_extra_time: boolean;
  concussion_subs_used: number;
}

interface ValidationResponse {
  is_valid: boolean;
  error_message?: string;
  team_status: SubstitutionStatus;
  opens_new_window: boolean;
}

interface SubstitutionFlowProps {
  matchId: string;
  team: Team;
  availablePlayers: Player[]; // All players in lineup
  onField: Set<string>; // Player IDs currently on field
  /** PMA-COCK-171: the rest of the club roster — players with no lineup
   * entry at all for this team. Rendered below a divider under the bench
   * list in step 2; picking one adds them to the bench (bench-only PATCH)
   * before the Substitution event is sent. */
  squadPlayers?: Player[];
  /** True while a squad pick's bench-PATCH is in flight. */
  isSubmittingSquadPlayer?: boolean;
  /** The server's error detail when a squad pick's bench-PATCH failed. */
  squadSubmitError?: string | null;
  expelledPlayerIds?: Set<string>;
  /**
   * PMA-COCK-120 (A2a): the tactical field's bench chips used to show a
   * passive yellow/red badge per substitute — the only live, unique
   * affordance those chips carried beyond what this modal's own one-tap
   * prefill already covers. Migrated here so an operator can still see, at
   * substitution time, that an incoming player is already booked.
   */
  disciplinaryStatusByPlayer?: Record<
    string,
    { yellowCount: number; red: boolean }
  >;
  period: number;
  globalClock: string;
  onSubmit: (
    playerOffId: string,
    playerOnId: string,
    isConcussion: boolean,
  ) => void;
  onCancel: () => void;
  initialPlayerOn?: Player | null;
  initialPlayerOff?: Player | null;
}

export default function SubstitutionFlow({
  matchId,
  team,
  availablePlayers,
  onField,
  squadPlayers = [],
  isSubmittingSquadPlayer = false,
  squadSubmitError = null,
  expelledPlayerIds,
  disciplinaryStatusByPlayer,
  period,
  globalClock,
  onSubmit,
  onCancel,
  initialPlayerOn,
  initialPlayerOff,
}: SubstitutionFlowProps) {
  const { t } = useTranslation("logger");

  const [step, setStep] = useState<"select-off" | "select-on" | "confirm">(
    "select-off",
  );
  const [playerOff, setPlayerOff] = useState<Player | null>(null);
  const [playerOn, setPlayerOn] = useState<Player | null>(
    initialPlayerOn || null,
  );
  const [isConcussion, setIsConcussion] = useState(false);
  const [validation, setValidation] = useState<ValidationResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [teamStatus, setTeamStatus] = useState<SubstitutionStatus>({
    total_substitutions: 0,
    windows_used: 0,
    is_extra_time: false,
    concussion_subs_used: 0,
  });

  useEffect(() => {
    setPlayerOff(initialPlayerOff || null);
    setPlayerOn(initialPlayerOn || null);
    setIsConcussion(false);
    setValidation(null);

    if (initialPlayerOff && initialPlayerOn) {
      setStep("confirm");
      return;
    }

    if (initialPlayerOff) {
      setStep("select-on");
      return;
    }

    setStep("select-off");
  }, [initialPlayerOff, initialPlayerOn]);

  const handleSelectOff = (player: Player) => {
    if (expelledPlayerIds?.has(player.id)) {
      return;
    }
    setPlayerOff(player);
    if (playerOn) {
      setStep("confirm");
    } else {
      setStep("select-on");
    }
  };

  const handleSelectOn = (player: Player) => {
    if (expelledPlayerIds?.has(player.id)) {
      return;
    }
    setPlayerOn(player);
    setStep("confirm");
  };

  const handleConfirm = () => {
    if (playerOff && playerOn && validation?.is_valid) {
      onSubmit(playerOff.id, playerOn.id, isConcussion);
    }
  };

  const handleBack = () => {
    if (step === "select-on") {
      if (initialPlayerOff) {
        setPlayerOn(null);
        setValidation(null);
        return;
      }
      setPlayerOff(null);
      setStep("select-off");
    } else if (step === "confirm") {
      if (initialPlayerOff) {
        setPlayerOn(null);
        setValidation(null);
        setStep("select-on");
      } else if (initialPlayerOn) {
        setPlayerOff(null);
        setValidation(null);
        setStep("select-off");
      } else {
        setPlayerOn(null);
        setValidation(null);
        setStep("select-on");
      }
    }
  };

  const localIsValid = Boolean(
    playerOff &&
      playerOn &&
      !expelledPlayerIds?.has(playerOff.id) &&
      !expelledPlayerIds?.has(playerOn.id) &&
      onField.has(playerOff.id) &&
      !onField.has(playerOn.id),
  );

  // PMA-COCK-171: a "squad pick" has no lineup entry for this team at all
  // (checked against `availablePlayers`, exactly like the parent hook's own
  // definition — they must stay in sync). The server's `/validate-
  // substitution` endpoint reasons about two EXISTING lineup players
  // swapping places and has no concept of a player who isn't in the lineup
  // yet, so it is skipped entirely for a squad pick; the real gate is the
  // bench-PATCH the parent runs on confirm (`squadSubmitError` below).
  const isSquadPick = Boolean(
    playerOn && !availablePlayers.some((p) => p.id === playerOn.id),
  );

  useEffect(() => {
    if (!playerOff || !playerOn || step !== "confirm") return;
    if (isSquadPick) {
      setValidation({
        is_valid: localIsValid,
        error_message: localIsValid
          ? undefined
          : t(
              "substitution.playerOffNotOnField",
              "Selected OFF player is not currently on the field.",
            ),
        team_status: teamStatus,
        opens_new_window: false,
      });
      return;
    }

    const baseValidation: ValidationResponse = localIsValid
      ? {
          is_valid: true,
          error_message: undefined,
          team_status: teamStatus,
          opens_new_window: false,
        }
      : {
          is_valid: false,
          error_message:
            expelledPlayerIds?.has(playerOff.id) ||
            expelledPlayerIds?.has(playerOn.id)
              ? t(
                  "substitution.expelledBlocked",
                  "Expelled players cannot be substituted.",
                )
              : onField.has(playerOn.id)
                ? t(
                    "substitution.playerAlreadyOn",
                    "Selected ON player is already on the field.",
                  )
                : t(
                    "substitution.playerOffNotOnField",
                    "Selected OFF player is not currently on the field.",
                  ),
          team_status: teamStatus,
          opens_new_window: false,
        };

    setValidation(baseValidation);

    const validateSubstitution = async () => {
      setIsValidating(true);
      try {
        const response = await fetchLoggerWithAuth(
          `${LOGGER_API_URL}/matches/${matchId}/validate-substitution`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              team_id: team.team_id ?? team.id,
              player_off_id: playerOff.id,
              player_on_id: playerOn.id,
              period: period,
              is_concussion: isConcussion,
              on_field_player_ids: Array.from(onField),
            }),
          },
        );

        const data = await response.json().catch(() => null);
        if (!response.ok) {
          const message =
            data?.detail ||
            data?.error_message ||
            t(
              "substitution.validationError",
              "Failed to validate substitution",
            );
          const serverStatus = data?.team_status || teamStatus;
          setValidation({
            is_valid: false,
            error_message: message,
            team_status: serverStatus,
            opens_new_window: Boolean(data?.opens_new_window),
          });
          setTeamStatus(serverStatus);
          return;
        }

        setValidation(data);
        setTeamStatus(data.team_status);
      } catch (error) {
        console.error("Validation failed:", error);
        const fallbackMessage =
          error instanceof Error
            ? error.message
            : t(
                "substitution.validationError",
                "Failed to validate substitution",
              );
        if (IS_E2E_TEST_MODE && localIsValid) {
          setValidation({
            ...baseValidation,
            is_valid: true,
            error_message: undefined,
          });
        } else {
          setValidation({
            is_valid: false,
            error_message: fallbackMessage,
            team_status: teamStatus,
            opens_new_window: false,
          });
        }
      } finally {
        setIsValidating(false);
      }
    };

    if (!localIsValid && !IS_E2E_TEST_MODE) {
      setValidation(baseValidation);
      return;
    }

    validateSubstitution();
  }, [
    playerOff,
    playerOn,
    step,
    isConcussion,
    matchId,
    team.id,
    period,
    t,
    onField,
    expelledPlayerIds,
    localIsValid,
    isSquadPick,
  ]);

  const confirmDisabled = isSubmittingSquadPlayer
    ? true
    : validation
      ? !validation.is_valid
      : !localIsValid;

  // Client wave 2026-09-19 (EPIC-61, brief 14/AC13): "an ascending ordered
  // list, like the lineup lists". Sorted strictly by `jersey_number` — the
  // AC's own wording ("visible ... in ascending order") names ascending
  // order as the whole criterion, not "position-grouped, ascending within
  // group" (`PlayerSelectorPanel.tsx`'s `sortPlayersByGroup`, the lineup
  // panel's own sort, groups first and would not read as "ascending" on a
  // roster whose jersey numbers don't happen to cluster by position). The
  // ROW visual idiom below is still borrowed from that same lineup list
  // (`renderPlayerGrid`) — a compact single-line row, not the old two-line
  // card — kept in the SAME responsive multi-column grid (unchanged below)
  // rather than forced to one column: this modal is wide (`max-w-4xl`), and
  // a strict single column would need MORE total vertical height for the
  // same player count, working against the "visible without scrolling"
  // half of the criterion. A CSS grid lays child elements out row-major
  // (left to right, then down), so ascending order is preserved visually
  // in reading order regardless of column count.
  // EPIC-95 T5: a squad player's sentinel `0` (see `formatJerseyBadge` above)
  // sorts to the END here, not the front — mirrors the identical
  // `Number(null) === 0` fix already applied to `sortRosterByJerseyNumber`
  // in `MatchesManager.tsx`/`PreMatchLineupPanel.tsx`.
  const byJerseyAscending = (a: Player, b: Player) => {
    const aRank = a.jersey_number > JERSEY_MISSING ? a.jersey_number : Infinity;
    const bRank = b.jersey_number > JERSEY_MISSING ? b.jersey_number : Infinity;
    return aRank - bRank;
  };
  // EPIC-94 (client wave 2026-09-23, AC7): the outgoing (select-off) list
  // now uses the SAME compact row idiom as the incoming (select-on) list
  // below, so it gets the same "ascending, like the lineup lists" ordering
  // PMA-COCK-211 already established for bench/squad — it was previously
  // unsorted (array order).
  const playersOnField = availablePlayers
    .filter((p) => onField.has(p.id) && !expelledPlayerIds?.has(p.id))
    .sort(byJerseyAscending);
  const playersOnBench = availablePlayers
    .filter((p) => !onField.has(p.id) && !expelledPlayerIds?.has(p.id))
    .sort(byJerseyAscending);
  // PMA-COCK-171: the rest of the club roster, below a divider under the
  // bench list — an operator who kicked off without substitutes previously
  // had no way to substitute at all. Squad players are never expelled (they
  // are not part of the match yet).
  const playersOnSquad = squadPlayers
    .filter((p) => !expelledPlayerIds?.has(p.id))
    .sort(byJerseyAscending);
  const noSubstituteCandidates =
    playersOnBench.length === 0 && playersOnSquad.length === 0;

  // Shared row renderer for both the select-off (outgoing) and select-on
  // (incoming/bench/squad) lists — EPIC-94 (client wave 2026-09-23, AC7)
  // generalizes what was `renderSelectOnOption` (client wave 2026-09-19,
  // brief 14/AC13, PMA-COCK-211) so the outgoing cards use the exact same
  // size and row style as the incoming ones: a compact single-row idiom
  // (borrowed from `PlayerSelectorPanel.tsx`'s `renderPlayerGrid` lineup
  // rows — smaller avatar, one line per player) so every candidate fits on
  // screen at the operator's tablet size without scrolling, same as the
  // lineup lists. `testIdPrefix` keeps each step's own testid scheme byte-
  // identical to before (`sub-on-{id}` / `sub-on-status-yellow-{id}` etc.
  // unchanged; `sub-off-{id}` unchanged, gains the same
  // `sub-off-status-yellow-{id}`/`sub-off-status-red-{id}` badge scheme).
  // The avatar/hover colors stay theme-appropriate (green for "coming on",
  // red/blue for "going off", matching the confirm step's own OFF/ON
  // color convention below) — only the SIZE and ROW STRUCTURE are shared.
  const renderPlayerRow = (
    player: Player,
    options: {
      testIdPrefix: "sub-on" | "sub-off";
      onClick: () => void;
      avatarBgClass: string;
      hoverBorderClass: string;
      hoverBgClass: string;
    },
  ) => {
    const {
      testIdPrefix,
      onClick,
      avatarBgClass,
      hoverBorderClass,
      hoverBgClass,
    } = options;
    const cardStatus = disciplinaryStatusByPlayer?.[player.id];
    const hasYellow = (cardStatus?.yellowCount ?? 0) > 0;
    const hasRed = Boolean(cardStatus?.red);
    return (
      <button
        key={player.id}
        onClick={onClick}
        data-testid={`${testIdPrefix}-${player.id}`}
        data-player-row="true"
        className={`w-full px-3 py-2 rounded-lg border-2 border-gray-300 dark:border-gray-600 ${hoverBorderClass} ${hoverBgClass} transition-all text-left`}
      >
        <div className="flex items-center gap-2.5">
          {/* T10 (EPIC-61 brief 15/AC14, client wave 2026-09-19, contrast
              audit fix): `bg-green-700` (not `bg-green-600`) is 5.02:1,
              clearing the 4.5:1 floor for 12px bold text. Reused verbatim
              for the outgoing row's own avatar color where applicable. */}
          <div
            className={`w-7 h-7 shrink-0 ${avatarBgClass} text-white rounded-full flex items-center justify-center text-xs font-bold`}
          >
            {formatJerseyBadge(player.jersey_number)}
          </div>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="font-semibold text-sm text-gray-900 dark:text-white truncate">
              {player.short_name || formatDisplayName(player.full_name)}
            </span>
            {hasYellow && (
              <span
                data-testid={`${testIdPrefix}-status-yellow-${player.id}`}
                className="inline-flex items-center gap-0.5 shrink-0"
              >
                {Array.from({
                  length: Math.max(1, cardStatus?.yellowCount || 0),
                }).map((_, index) => (
                  <span
                    key={`${player.id}-yellow-${index}`}
                    className="h-2.5 w-1.5 rounded-sm bg-yellow-400"
                  />
                ))}
              </span>
            )}
            {hasRed && (
              <span
                data-testid={`${testIdPrefix}-status-red-${player.id}`}
                className="inline-flex items-center shrink-0"
              >
                <span className="h-2.5 w-1.5 rounded-sm bg-red-500" />
              </span>
            )}
          </div>
          <span className="text-xs text-gray-600 dark:text-gray-400 shrink-0">
            {player.position}
          </span>
        </div>
      </button>
    );
  };

  const renderSelectOnOption = (player: Player) =>
    renderPlayerRow(player, {
      testIdPrefix: "sub-on",
      onClick: () => handleSelectOn(player),
      avatarBgClass: "bg-green-700",
      hoverBorderClass: "hover:border-green-500",
      hoverBgClass: "hover:bg-green-50 dark:hover:bg-green-900",
    });

  const renderSelectOffOption = (player: Player) =>
    renderPlayerRow(player, {
      testIdPrefix: "sub-off",
      onClick: () => handleSelectOff(player),
      avatarBgClass: "bg-red-500",
      hoverBorderClass: "hover:border-blue-500",
      hoverBgClass: "hover:bg-blue-50 dark:hover:bg-blue-900",
    });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      data-testid="substitution-modal"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90dvh] flex flex-col overflow-hidden">
        {/* EPIC-96 (AC5, tablet confirm-step overflow): the card is a flex
            column now, not a single scrolling box — `dvh` (not `vh`, which on
            tablet Safari resolves to the LARGE viewport, toolbars included)
            caps the card at 90% of the real visible height, and only the
            middle step-content region (below) scrolls. The header, stepper,
            and footer are `shrink-0` flex siblings, so "Confirmar
            Sustitución" is always inside the card's own bounds regardless of
            how tall a given step's content is. */}
        <div className="shrink-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users size={28} />
              <div>
                <h2
                  className="text-2xl font-bold"
                  data-testid="substitution-heading"
                >
                  {t("substitution.title", "Substitution")}
                </h2>
                <p className="text-blue-100 text-sm">
                  {team.name} • {globalClock} •{" "}
                  {t(`period.${period}`, `Period ${period}`)}
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Counts, not budgets. The old "4/5 substitutions, 2/3 windows"
              tiles implied a cap that no longer exists and would have gone
              on counting down to a limit nothing enforces. */}
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div className="bg-blue-800 bg-opacity-50 rounded p-3">
              <div className="text-blue-200 text-xs mb-1">
                {t("substitution.subsMade", "Substitutions made")}
              </div>
              <div
                className="text-2xl font-bold"
                data-testid="substitution-total-count"
              >
                {teamStatus.total_substitutions}
              </div>
            </div>
            <div className="bg-blue-800 bg-opacity-50 rounded p-3">
              <div className="text-blue-200 text-xs mb-1">
                {t("substitution.concussion", "Concussion")}
              </div>
              <div className="text-2xl font-bold">
                {teamStatus.concussion_subs_used}
              </div>
            </div>
          </div>
        </div>

        <div className="shrink-0 flex items-center justify-center gap-3 p-3 bg-gray-50 dark:bg-gray-900">
          <div
            className={`flex items-center gap-2 ${
              step === "select-off"
                ? "text-blue-600 font-semibold"
                : "text-gray-400"
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === "select-off" ? "bg-blue-600 text-white" : "bg-gray-300"
              }`}
            >
              1
            </div>
            <span>{t("substitution.selectPlayerOff", "Player Off")}</span>
          </div>
          <ArrowRight size={20} className="text-gray-400" />
          <div
            className={`flex items-center gap-2 ${
              step === "select-on"
                ? "text-blue-600 font-semibold"
                : "text-gray-400"
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === "select-on" ? "bg-blue-600 text-white" : "bg-gray-300"
              }`}
            >
              2
            </div>
            <span>{t("substitution.selectPlayerOn", "Player On")}</span>
          </div>
          <ArrowRight size={20} className="text-gray-400" />
          <div
            className={`flex items-center gap-2 ${
              step === "confirm"
                ? "text-blue-600 font-semibold"
                : "text-gray-400"
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === "confirm" ? "bg-blue-600 text-white" : "bg-gray-300"
              }`}
            >
              3
            </div>
            <span>{t("substitution.confirm", "Confirm")}</span>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {step === "select-off" && (
            <div data-testid="substitution-step-off">
              {playerOn && (
                <div className="flex items-center gap-3 mb-4 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-300 dark:border-green-700">
                  <div className="w-8 h-8 bg-green-700 text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">
                    {formatJerseyBadge(playerOn.jersey_number)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-green-700 dark:text-green-400 font-medium uppercase tracking-wide">
                      {t("substitution.subbingIn", "Subbing in")}
                    </p>
                    <p className="text-sm font-semibold text-green-900 dark:text-green-100 truncate">
                      {playerOn.short_name ||
                        formatDisplayName(playerOn.full_name)}
                    </p>
                  </div>
                </div>
              )}
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                {t(
                  "substitution.whoIsLeavingField",
                  "Who is leaving the field?",
                )}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {playersOnField.map((player) => renderSelectOffOption(player))}
              </div>
            </div>
          )}

          {step === "select-on" && (
            <div data-testid="substitution-step-on">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                {t(
                  "substitution.whoIsEnteringField",
                  "Who is entering the field?",
                )}
              </h3>

              {noSubstituteCandidates ? (
                <p
                  className="text-sm text-gray-600 dark:text-gray-400 p-4 rounded-lg border border-dashed border-gray-300 dark:border-gray-600"
                  data-testid="substitution-no-candidates"
                >
                  {t(
                    "substitution.noCandidates",
                    "No substitutes available. Add players to the bench from the lineup panel.",
                  )}
                </p>
              ) : (
                <>
                  {playersOnBench.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {playersOnBench.map((player) =>
                        renderSelectOnOption(player),
                      )}
                    </div>
                  )}

                  {playersOnSquad.length > 0 && (
                    <>
                      <div
                        className="flex items-center gap-3 my-4"
                        data-testid="substitution-squad-divider"
                      >
                        <div className="flex-1 border-t border-gray-300 dark:border-gray-600" />
                        <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {t(
                            "substitution.notInSquad",
                            "Not in the match squad",
                          )}
                        </span>
                        <div className="flex-1 border-t border-gray-300 dark:border-gray-600" />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {playersOnSquad.map((player) =>
                          renderSelectOnOption(player),
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {step === "confirm" && playerOff && playerOn && (
            <div data-testid="substitution-step-confirm" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg">
                  <div className="text-sm text-red-700 dark:text-red-300 font-semibold mb-2">
                    {t("substitution.off", "OFF")}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                      {formatJerseyBadge(playerOff.jersey_number)}
                    </div>
                    <div>
                      <div className="font-bold text-lg text-gray-900 dark:text-white">
                        {playerOff.short_name ||
                          formatDisplayName(playerOff.full_name)}
                      </div>
                      <div className="text-gray-600 dark:text-gray-400">
                        {playerOff.position}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg">
                  <div className="text-sm text-green-700 dark:text-green-300 font-semibold mb-2">
                    {t("substitution.on", "ON")}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 bg-green-700 text-white rounded-full flex items-center justify-center font-bold text-xl">
                      {formatJerseyBadge(playerOn.jersey_number)}
                    </div>
                    <div>
                      <div className="font-bold text-lg text-gray-900 dark:text-white">
                        {playerOn.short_name ||
                          formatDisplayName(playerOn.full_name)}
                      </div>
                      <div className="text-gray-600 dark:text-gray-400">
                        {playerOn.position}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-900 border border-yellow-300 dark:border-yellow-700 rounded-lg">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isConcussion}
                    onChange={(e) => setIsConcussion(e.target.checked)}
                    className="w-5 h-5 text-blue-600"
                  />
                  <div className="flex items-center gap-2">
                    <ShieldAlert size={20} className="text-yellow-600" />
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {t(
                          "substitution.concussionSubstitution",
                          "Concussion Substitution",
                        )}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {t(
                          "substitution.concussionNote",
                          "Does not count toward maximum. Opponent receives additional substitution.",
                        )}
                      </div>
                    </div>
                  </div>
                </label>
              </div>

              {isValidating && (
                <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg text-center">
                  <div className="animate-spin inline-block w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                  <div className="mt-2 text-gray-600 dark:text-gray-400">
                    {t("substitution.validating", "Validating...")}
                  </div>
                </div>
              )}

              {validation && !isValidating && (
                <div>
                  {!validation.is_valid && (
                    <div className="p-4 bg-red-50 dark:bg-red-900 border border-red-300 dark:border-red-700 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle
                          size={24}
                          className="text-red-600 flex-shrink-0 mt-0.5"
                        />
                        <div>
                          <div className="font-semibold text-red-800 dark:text-red-200 mb-1">
                            {t("substitution.invalid", "Invalid Substitution")}
                          </div>
                          <div
                            className="text-red-700 dark:text-red-300"
                            data-testid="substitution-validation-error"
                          >
                            {validation.error_message}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {validation.is_valid && (
                    <div className="p-4 bg-green-50 dark:bg-green-900 border border-green-300 dark:border-green-700 rounded-lg">
                      <div className="flex items-center gap-3 text-green-800 dark:text-green-200">
                        <div className="w-6 h-6 bg-green-700 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm">✓</span>
                        </div>
                        <span className="font-semibold">
                          {t("substitution.valid", "Substitution is valid")}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isSubmittingSquadPlayer && (
                <div
                  className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg text-center"
                  data-testid="substitution-squad-submitting"
                >
                  <div className="animate-spin inline-block w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                  <div className="mt-2 text-gray-600 dark:text-gray-400">
                    {t("substitution.addingToBench", "Adding to the bench...")}
                  </div>
                </div>
              )}

              {squadSubmitError && !isSubmittingSquadPlayer && (
                <div className="p-4 bg-red-50 dark:bg-red-900 border border-red-300 dark:border-red-700 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle
                      size={24}
                      className="text-red-600 flex-shrink-0 mt-0.5"
                    />
                    <div>
                      <div className="font-semibold text-red-800 dark:text-red-200 mb-1">
                        {t("substitution.invalid", "Invalid Substitution")}
                      </div>
                      <div
                        className="text-red-700 dark:text-red-300"
                        data-testid="substitution-squad-submit-error"
                      >
                        {squadSubmitError}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="shrink-0 bg-gray-100 dark:bg-gray-900 p-4 rounded-b-lg border-t border-gray-300 dark:border-gray-700">
          <div className="flex gap-3 justify-end">
            <button
              onClick={onCancel}
              data-testid="cancel-substitution"
              className="px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 font-semibold transition-colors"
            >
              {t("common.cancel", "Cancel")}
            </button>
            {step !== "select-off" && (
              <button
                onClick={handleBack}
                className="px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 font-semibold transition-colors"
              >
                {t("common.back", "Back")}
              </button>
            )}
            {step === "confirm" && (
              <button
                onClick={handleConfirm}
                disabled={confirmDisabled}
                data-testid="confirm-substitution"
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("substitution.confirmSubmit", "Confirm Substitution")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
